import React, { useState, useEffect } from 'react';
import { RiAddFill } from "react-icons/ri";
import { BsDash } from "react-icons/bs";
import { IoAddCircleOutline } from "react-icons/io5";
import { inventoryService } from '../../../../Services/combineInventoryService'; 
import { toast } from "react-toastify";
import { t } from "../../../../utils/translate";
import noDataIcon from "../../../../Assets/no_data_found.webp";
import { GiClick } from 'react-icons/gi';

const InventorySelection = ({ 
  items = [],
  setItems,
  activeProduct,
  setActiveProduct,
  currentInventoryIndex,
  setCurrentInventoryIndex 
}) => {
  const [inventories, setInventories] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Load inventories on component mount
  useEffect(() => {
    loadInventories();
  }, []);

  // Load inventory items when inventory is selected
  useEffect(() => {
    if (currentInventoryIndex !== null && inventories[currentInventoryIndex]) {
      setSelectedInventory(inventories[currentInventoryIndex]);
      loadInventoryItems(inventories[currentInventoryIndex]._id);
    }
  }, [currentInventoryIndex, inventories]);

  // Load inventories from API
  const loadInventories = async () => {
    setLoading(true);
    try {
      const result = await inventoryService.mainInventory.getAll({
        status: 'active',
        limit: 50
      });

      if (result.success) {
        setInventories(result.data);
      } else {
        toast.error(result.message || 'Failed to load inventories');
      }
    } catch (error) {
      toast.error('Failed to load inventories');
      console.error('Error loading inventories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load items for selected inventory
  const loadInventoryItems = async (inventoryId) => {
    setLoadingItems(true);
    try {
      const result = await inventoryService.items.getByMainInventoryId(inventoryId, {
        limit: 100
      });

      if (result.success) {
        // Add available quantity tracking for display
        const itemsWithTracking = result.data.map(item => ({
          ...item,
          originalQuantity: item.quantityAvailable,
          displayQuantity: item.quantityAvailable
        }));
        setInventoryItems(itemsWithTracking);
      } else {
        toast.error(result.message || 'Failed to load inventory items');
      }
    } catch (error) {
      toast.error('Failed to load inventory items');
      console.error('Error loading inventory items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  // Handle quantity increment
  const handleIncrement = (inventoryItem) => {
    const currentItemInList = items.find(item => item._id === inventoryItem._id);
    const currentQuantity = currentItemInList ? currentItemInList.quantity : 0;
    
    // Check available quantity (considering what's already selected)
    const availableForSelection = inventoryItem.originalQuantity - currentQuantity;
    
    if (availableForSelection <= 0) {
      toast.warning('No more quantity available for this item');
      return;
    }

    // Update main items list
    const updatedItems = [...items];
    const itemIndex = updatedItems.findIndex(item => item._id === inventoryItem._id);

    if (itemIndex !== -1) {
      updatedItems[itemIndex].quantity++;
      updatedItems[itemIndex].kg = inventoryItem.weight;
    } else {
      updatedItems.push({
        ...inventoryItem,
        id: inventoryItem._id,
        quantity: 1,
        kg: inventoryItem.weight,
        date: new Date().toLocaleDateString(),
        category: "Inventory",
        source: "inventory",
        inventoryItemId: inventoryItem._id,
        mainInventoryId: selectedInventory._id,
        materialNo: inventoryItem.materialNo,
        materialName: inventoryItem.materialName,
        description: inventoryItem.description,
        weight: inventoryItem.weight,
        inventoryListName: inventoryItem.inventoryListName,
        productName: inventoryItem.materialName, // For compatibility
        product_no: inventoryItem.materialNo // For compatibility
      });
    }

    setItems(updatedItems);

    // Update display quantity in inventory items
    const updatedInventoryItems = inventoryItems.map(item => {
      if (item._id === inventoryItem._id) {
        return {
          ...item,
          displayQuantity: item.originalQuantity - (currentQuantity + 1)
        };
      }
      return item;
    });
    setInventoryItems(updatedInventoryItems);
  };

  // Handle quantity decrement
  const handleDecrement = (inventoryItem) => {
    const currentItemInList = items.find(item => item._id === inventoryItem._id);
    if (!currentItemInList || currentItemInList.quantity <= 0) {
      return;
    }

    // Update main items list
    const updatedItems = [...items];
    const itemIndex = updatedItems.findIndex(item => item._id === inventoryItem._id);

    if (itemIndex !== -1) {
      if (updatedItems[itemIndex].quantity > 1) {
        updatedItems[itemIndex].quantity--;
        updatedItems[itemIndex].kg = inventoryItem.weight;
      } else {
        updatedItems.splice(itemIndex, 1);
      }
      setItems(updatedItems);
    }

    // Update display quantity in inventory items
    const newQuantityInList = currentItemInList.quantity - 1;
    const updatedInventoryItems = inventoryItems.map(item => {
      if (item._id === inventoryItem._id) {
        return {
          ...item,
          displayQuantity: item.originalQuantity - Math.max(0, newQuantityInList)
        };
      }
      return item;
    });
    setInventoryItems(updatedInventoryItems);
  };

  // Handle manual input change
  const handleInputChange = (e, inventoryItem) => {
    const inputValue = e.target.value;
    const numericValue = parseInt(inputValue, 10) || 0;
    
    if (numericValue > inventoryItem.originalQuantity) {
      toast.warning(`Only ${inventoryItem.originalQuantity} items available`);
      return;
    }

    // Update main items list
    const updatedItems = [...items];
    const itemIndex = updatedItems.findIndex(item => item._id === inventoryItem._id);

    if (numericValue > 0) {
      if (itemIndex !== -1) {
        updatedItems[itemIndex].quantity = numericValue;
        updatedItems[itemIndex].kg = inventoryItem.weight;
      } else {
        updatedItems.push({
          ...inventoryItem,
          id: inventoryItem._id,
          quantity: numericValue,
          kg: inventoryItem.weight,
          date: new Date().toLocaleDateString(),
          category: "Inventory",
          source: "inventory",
          inventoryItemId: inventoryItem._id,
          mainInventoryId: selectedInventory._id,
          materialNo: inventoryItem.materialNo,
          materialName: inventoryItem.materialName,
          description: inventoryItem.description,
          weight: inventoryItem.weight,
          inventoryListName: inventoryItem.inventoryListName,
          productName: inventoryItem.materialName, // For compatibility
          product_no: inventoryItem.materialNo // For compatibility
        });
      }
    } else {
      if (itemIndex !== -1) {
        updatedItems.splice(itemIndex, 1);
      }
    }

    setItems(updatedItems);

    // Update display quantity
    const updatedInventoryItems = inventoryItems.map(item => {
      if (item._id === inventoryItem._id) {
        return {
          ...item,
          displayQuantity: item.originalQuantity - numericValue
        };
      }
      return item;
    });
    setInventoryItems(updatedInventoryItems);
  };

  // Get current quantity for an item
  const getCurrentQuantity = (inventoryItemId) => {
    const currentItem = items.find(item => item._id === inventoryItemId);
    return currentItem ? currentItem.quantity : 0;
  };

  return (
    <>
      {/* Inventory Selection Section - Similar to Custom List */}
      <div className="py-[30px] lg:py-[100px] border-b border-b-[#CCCCCC]">
        <div className="custom-container">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-[30px] lg:gap-[70px]">
            <div>
              <p className="medium-title text-nowrap">{t("inventory_list") || "Inventory List"}</p>
            </div>
            <div className="flex flex-wrap w-full gap-[20px] lg:gap-[40px] md:pl-16">
              {loading ? (
                <div className="flex justify-center items-center w-full py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading inventories...</span>
                </div>
              ) : inventories && inventories.length > 0 ? (
                inventories.map((inventory, index) => (
                  <div
                    key={inventory._id}
                    onClick={() => {
                      setCurrentInventoryIndex(index);
                      setActiveProduct(5); // Set to inventory mode (5 for inventory)
                    }}
                    className={`w-[calc(50%-10px)] lg:w-[180px] py-[20px] cursor-pointer px-[20px] lg:px-[40px] max-w-[180px] ${
                      index === currentInventoryIndex
                        ? "border-[#0072BB26] bg-[#0072BB26]"
                        : "border-[#CCCCCC]"
                    } border rounded-[10px] flex flex-col gap-[10px] lg:gap-[30px] text-center relative transition-all duration-200 hover:shadow-md`}
                  >
                    <div className="flex flex-col items-center justify-center">
                      {/* Inventory Icon */}
                      <div className="w-[80px] h-[80px] mx-auto mb-2 bg-gray-100 rounded-lg flex items-center justify-center">
                        <GiClick 
                          size={40} 
                          color={index === currentInventoryIndex ? "#0072BB" : "#666"}
                        />
                      </div>
                      
                      {/* Inventory Name */}
                      <p className={`text-xs font-semibold uppercase ${
                        index === currentInventoryIndex ? "text-[#0072BB]" : ""
                      } overflow-hidden break-words text-center`}>
                        {inventory.inventoryName}
                      </p>
                      
                      {/* Inventory Stats */}
                      <div className="mt-2 text-xs text-gray-600 space-y-1">
                        <p>{inventory.itemCount || 0} items</p>
                        <p>{inventory.totalWeight || 0} kg</p>
                      </div>
                      
                      {/* Description */}
                      {inventory.description && (
                        <p className={`text-[10px] font-semibold mb-3 lg:mb-0 ${
                          index === currentInventoryIndex ? "text-[#0072BB]" : ""
                        } overflow-hidden break-words text-center`}>
                          {inventory.description.length > 50 
                            ? inventory.description.substring(0, 50) + '...' 
                            : inventory.description}
                        </p>
                      )}
                    </div>

                    {/* Favorite indicator */}
                    {inventory.isFavorite && (
                      <div className="absolute top-2 right-2">
                        <span className="text-yellow-500 text-sm">⭐</span>
                      </div>
                    )}

                    {/* Status indicator */}
                    <div className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${
                      inventory.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {inventory.status}
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-full text-center py-12">
                  <IoAddCircleOutline size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 text-lg font-semibold">No Inventories Available</p>
                  <p className="text-gray-500 text-sm mt-2">Create inventories first to select items</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Selected Inventory Items Display - Similar to Custom List Table */}
      {activeProduct === 5 && selectedInventory && (
        <div className="select-none border-b border-[#CCCCCC]">
          <div className="custom-container py-[30px] lg:py-[50px]">
            <div className="text-center mb-6">
              <p className="medium-title underline">
                {selectedInventory.inventoryName} - {t("inventory_items") || "Inventory Items"}
              </p>
            </div>

            {loadingItems ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading items...</span>
              </div>
            ) : inventoryItems?.length > 0 ? (
              <div className="w-full flex flex-col mt-10">
                <div className="-m-1.5 overflow-x-auto">
                  <div className="p-1.5 min-w-full inline-block align-middle">
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                        <thead>
                          <tr>
                            <th className="px-6 py-3 text-start text-xs font-bold text-black uppercase dark:text-black sm:table-cell hidden">
                              {t("materialNo") || "Material No"}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-bold text-black uppercase dark:text-black">
                              {t("materialName") || "Material Name"}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-bold text-black uppercase dark:text-black sm:table-cell hidden">
                              {t("descriptionName") || "Description"}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-bold text-black uppercase dark:text-black sm:table-cell hidden">
                              {t("weightInKG") || "Weight (kg)"}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-bold text-black uppercase dark:text-black sm:table-cell hidden">
                              {t("available") || "Available"}
                            </th>
                            <th className="px-6 py-3 text-start text-xs font-bold text-black uppercase dark:text-black">
                              Quantity
                            </th>
                          </tr>
                        </thead>
                        <tbody className="border-b border-b-[#CCCCCC]">
                          {inventoryItems.map((item, index) => {
                            const currentQuantity = getCurrentQuantity(item._id);
                            const hasQuantity = currentQuantity > 0;
                            
                            return (
                              <tr 
                                key={item._id} 
                                className="odd:bg-white even:bg-gray-100 odd:hover:bg-blue-200 even:hover:bg-blue-200 transition-colors duration-200 ease-in-out"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-black dark:text-black sm:table-cell hidden">
                                  <p className="normal-text">
                                    {item.materialNo || "Not Defined"}
                                  </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-black dark:text-black">
                                  <p className="normal-text">
                                    {item.materialName}
                                  </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-black dark:text-black sm:table-cell hidden">
                                  <p className="normal-text">
                                    {item.description}
                                  </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-black dark:text-black sm:table-cell hidden">
                                  <p className="normal-text">
                                    {item.weight}
                                  </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-black dark:text-black sm:table-cell hidden">
                                  <p className={`normal-text font-medium ${
                                    item.displayQuantity === 0 
                                      ? 'text-red-600' 
                                      : item.displayQuantity < 10 
                                      ? 'text-yellow-600' 
                                      : 'text-green-600'
                                  }`}>
                                    {item.displayQuantity}
                                  </p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-black dark:text-black">
                                  <div className="flex gap-[19px] items-center">
                                    {/* Decrease Button */}
                                    <div
                                      onClick={() => handleDecrement(item)}
                                      className={`${
                                        hasQuantity 
                                          ? "border-[#0072BB]" 
                                          : "border-[#000000]"
                                      } cursor-pointer h-[20px] w-[20px] border-[2px] rounded-[5px] flex justify-center items-center`}
                                    >
                                      <BsDash
                                        size={18}
                                        color={hasQuantity ? "#0072BB" : "#000000"}
                                      />
                                    </div>

                                    {/* Quantity Input */}
                                    <input
                                      className={`${
                                        hasQuantity 
                                          ? "!text-[#0072BB] !border-[#0072BB]" 
                                          : "text-[#000000]"
                                      } normal-text`}
                                      type="text"
                                      value={currentQuantity.toString().padStart(2, "0")}
                                      onChange={(e) => handleInputChange(e, item)}
                                      placeholder="00"
                                      style={{
                                        width: "40px",
                                        padding: "8px",
                                        height: "32px",
                                      }}
                                    />

                                    {/* Increase Button */}
                                    <div
                                      onClick={() => handleIncrement(item)}
                                      className={`${
                                        item.displayQuantity > 0
                                          ? "border-[#0072BB]"
                                          : "border-[#CCCCCC]"
                                      } cursor-pointer h-[20px] w-[20px] border-[2px] rounded-[5px] flex justify-center items-center ${
                                        item.displayQuantity === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}
                                    >
                                      <RiAddFill
                                        size={18}
                                        color={item.displayQuantity > 0 ? "#0072BB" : "#CCCCCC"}
                                      />
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <h1 className="flex flex-col gap-1 justify-center items-center font-bold text-lg mb-10 pb-5 border rounded-2xl text-gray-400">
                <img
                  src={noDataIcon}
                  alt="no_data_image"
                  className="w-[200px]"
                />
                No Items Available in this Inventory
              </h1>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InventorySelection;